package top.wcpe.allincore.dsl.ir.expr

import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import kotlin.jvm.JvmField
import kotlin.jvm.JvmOverloads

/**
 * 表达式节点（客户端解释器消费）。
 *
 * 与 `dsl-compiler` 的 `ExprSerializer` 字节格式对偶：
 * 编译器侧序列化后的 byte[] 由 [ExprBlobReader] 解析为本类型树。
 *
 * 采用 interface + 嵌套 class 子类模拟 sealed（沿用原 Java 8 形态，保 Java 互操作）。
 */
interface ExprNode {

    /** 字面量：值类型为 String / Double / Boolean。 */
    class Literal(val value: Any?) : ExprNode {
        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is Literal) return false
            return value == other.value
        }

        override fun hashCode(): Int = value?.hashCode() ?: 0
    }

    /** `state.<name>`。 */
    class StateRef(name: String?) : ExprNode {
        val name: String = name ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean = other is StateRef && name == other.name

        override fun hashCode(): Int = name.hashCode()
    }

    /** `params.<name>` 打开参数只读引用。 */
    class ParamsRef(name: String?) : ExprNode {
        val name: String = name ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean = other is ParamsRef && name == other.name

        override fun hashCode(): Int = name.hashCode() xor 0x5041524D
    }

    /**
     * `session.<name>`：跨 screen / hud 共享的同名 Signal 引用。
     *
     * 由 runtime ExprEvaluator 路由到 EvalContext.sessionStates。未声明字段读取返回 null
     * （与 StateRef 一致，runtime 层做 reactive tracking 透明降级）。
     */
    class SessionRef(name: String?) : ExprNode {
        val name: String = name ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean = other is SessionRef && name == other.name

        override fun hashCode(): Int = name.hashCode() xor 0x53455353 // "SESS"
    }

    /**
     * `persist.<name>`：持久化到本地 JSON 的字段引用。
     *
     * 由 runtime ExprEvaluator 路由到 EvalContext.persistStates。runtime 持有
     * PersistentStateStore 决定 Signal 来源（玩家 JOIN 时按 serverKey + uuid 加载）。
     */
    class PersistRef(name: String?) : ExprNode {
        val name: String = name ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean = other is PersistRef && name == other.name

        override fun hashCode(): Int = name.hashCode() xor 0x50455253 // "PERS"
    }

    /** `derived.<name>` 派生值只读引用。运行时解析为对应 Computed 并 .get() 触发追踪。 */
    class DerivedRef(name: String?) : ExprNode {
        val name: String = name ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean = other is DerivedRef && name == other.name

        override fun hashCode(): Int = name.hashCode() xor 0x44455256 // "DERV"
    }

    /** 字符串插值，由文本片段或嵌套表达式片段组成。 */
    class Interp(parts: List<Part>) : ExprNode {
        val parts: List<Part> = ReadOnlyList(ArrayList(parts))

        /** 插值片段。 */
        interface Part

        /** 纯文本片段。 */
        class TextPart(text: String?) : Part {
            val text: String = text ?: throw NullPointerException()

            override fun equals(other: Any?): Boolean = other is TextPart && text == other.text

            override fun hashCode(): Int = text.hashCode()
        }

        /** 嵌入的子表达式片段。 */
        class ExprPart(expr: ExprNode?) : Part {
            val expr: ExprNode = expr ?: throw NullPointerException()

            override fun equals(other: Any?): Boolean = other is ExprPart && expr == other.expr

            override fun hashCode(): Int = expr.hashCode()
        }

        override fun equals(other: Any?): Boolean = other is Interp && parts == other.parts

        override fun hashCode(): Int = parts.hashCode()
    }

    /** 成员访问，例如 `payload.tick`、`payload.error`。 */
    class MemberAccess(target: ExprNode?, member: String?) : ExprNode {
        val target: ExprNode = target ?: throw NullPointerException()
        val member: String = member ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is MemberAccess) return false
            return target == other.target && member == other.member
        }

        // 复刻 Objects.hash(target, member)：种子 1、31 倍累乘。
        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + target.hashCode()
            result = 31 * result + member.hashCode()
            return result
        }

        override fun toString(): String = "MemberAccess(" + target + "." + member + ")"
    }

    /**
     * 裸标识符引用，用于 request 回调参数的根访问。
     *
     * 例如 `payload.tick` 在树中表现为 `MemberAccess(IdentRef("payload"), "tick")`。
     */
    class IdentRef(name: String?) : ExprNode {
        val name: String = name ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean = other is IdentRef && name == other.name

        override fun hashCode(): Int = name.hashCode()

        override fun toString(): String = "IdentRef(" + name + ")"
    }

    /**
     * 主题 token 引用，例如 `theme.primary`。
     *
     * 由 M6b 引入；运行期由 ExprEvaluator 根据 `UiIr.themeTokens` 解析为字面量值。
     */
    class ThemeRef(key: String?) : ExprNode {
        @JvmField
        val key: String = key ?: throw NullPointerException("key")

        fun getKey(): String = key

        override fun equals(other: Any?): Boolean = other is ThemeRef && key == other.key

        override fun hashCode(): Int = key.hashCode()

        override fun toString(): String = "ThemeRef(" + key + ")"
    }

    /** 二元运算，MVP 仅 `+`。 */
    class BinaryOp(op: String?, left: ExprNode?, right: ExprNode?) : ExprNode {
        val op: String = op ?: throw NullPointerException()
        val left: ExprNode = left ?: throw NullPointerException()
        val right: ExprNode = right ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is BinaryOp) return false
            return op == other.op && left == other.left && right == other.right
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + op.hashCode()
            result = 31 * result + left.hashCode()
            result = 31 * result + right.hashCode()
            return result
        }
    }

    /**
     * 成员方法调用，例如 `local.openScreen("id")`、`local.back()`。
     *
     * MVP 仅由运行时根据 [target] 类型分派内建方法（如 LocalApi）；
     * 不支持通用闭包/反射调用语义。
     */
    class MemberCall(
        target: ExprNode?,
        method: String?,
        args: List<ExprNode>?,
        namedArgs: List<NamedArg>?,
    ) : ExprNode {
        val target: ExprNode = target ?: throw NullPointerException()
        val method: String = method ?: throw NullPointerException()
        val args: List<ExprNode> = ReadOnlyList(ArrayList(args ?: throw NullPointerException()))
        val namedArgs: List<NamedArg> = ReadOnlyList(ArrayList(namedArgs ?: throw NullPointerException()))

        /** 旧构造器：无命名实参；保留向后兼容。 */
        constructor(target: ExprNode?, method: String?, args: List<ExprNode>?) :
            this(target, method, args, emptyList())

        override fun equals(other: Any?): Boolean {
            if (other !is MemberCall) return false
            return target == other.target && method == other.method &&
                args == other.args && namedArgs == other.namedArgs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + target.hashCode()
            result = 31 * result + method.hashCode()
            result = 31 * result + args.hashCode()
            result = 31 * result + namedArgs.hashCode()
            return result
        }

        override fun toString(): String =
            "MemberCall(" + target + "." + method + args +
                (if (namedArgs.isEmpty()) "" else ",named=" + namedArgs) + ")"

        /** Stage 4：命名实参 `name = value`。 */
        class NamedArg(name: String?, value: ExprNode?) {
            val name: String = name ?: throw NullPointerException()
            val value: ExprNode = value ?: throw NullPointerException()

            override fun equals(other: Any?): Boolean {
                if (other !is NamedArg) return false
                return name == other.name && value == other.value
            }

            override fun hashCode(): Int {
                var result = 1
                result = 31 * result + name.hashCode()
                result = 31 * result + value.hashCode()
                return result
            }

            override fun toString(): String = "$name=$value"
        }
    }

    // ===== C1 新增节点 =====

    /** `null` 字面量。 */
    class NullLit private constructor() : ExprNode {
        override fun equals(other: Any?): Boolean = other is NullLit

        override fun hashCode(): Int = 0

        override fun toString(): String = "NullLit"

        companion object {
            @JvmField
            val INSTANCE = NullLit()
        }
    }

    /** 一元运算：`-` 数值取反 或 `!` 逻辑非。 */
    class UnaryOp(op: String?, operand: ExprNode?) : ExprNode {
        val op: String = op ?: throw NullPointerException()
        val operand: ExprNode = operand ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is UnaryOp) return false
            return op == other.op && operand == other.operand
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + op.hashCode()
            result = 31 * result + operand.hashCode()
            return result
        }

        override fun toString(): String = "UnaryOp(" + op + operand + ")"
    }

    /** `target?.member`：target 为 null 时短路返回 null。 */
    class SafeMemberAccess(target: ExprNode?, member: String?) : ExprNode {
        val target: ExprNode = target ?: throw NullPointerException()
        val member: String = member ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is SafeMemberAccess) return false
            return target == other.target && member == other.member
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + target.hashCode()
            result = 31 * result + member.hashCode()
            return result
        }
    }

    /** `target?.method(args)`：target 为 null 时短路返回 null。 */
    class SafeMemberCall(target: ExprNode?, method: String?, args: List<ExprNode>?) : ExprNode {
        val target: ExprNode = target ?: throw NullPointerException()
        val method: String = method ?: throw NullPointerException()
        val args: List<ExprNode> = ReadOnlyList(ArrayList(args ?: throw NullPointerException()))

        override fun equals(other: Any?): Boolean {
            if (other !is SafeMemberCall) return false
            return target == other.target && method == other.method && args == other.args
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + target.hashCode()
            result = 31 * result + method.hashCode()
            result = 31 * result + args.hashCode()
            return result
        }
    }

    /** `left ?: right`：left 非 null 取 left，否则取 right。 */
    class Elvis(left: ExprNode?, right: ExprNode?) : ExprNode {
        val left: ExprNode = left ?: throw NullPointerException()
        val right: ExprNode = right ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is Elvis) return false
            return left == other.left && right == other.right
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + left.hashCode()
            result = 31 * result + right.hashCode()
            return result
        }
    }

    /** `cond ? thenExpr : elseExpr` 三元。 */
    class Ternary(cond: ExprNode?, thenExpr: ExprNode?, elseExpr: ExprNode?) : ExprNode {
        val cond: ExprNode = cond ?: throw NullPointerException()
        val thenExpr: ExprNode = thenExpr ?: throw NullPointerException()
        val elseExpr: ExprNode = elseExpr ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is Ternary) return false
            return cond == other.cond && thenExpr == other.thenExpr && elseExpr == other.elseExpr
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + cond.hashCode()
            result = 31 * result + thenExpr.hashCode()
            result = 31 * result + elseExpr.hashCode()
            return result
        }
    }

    /** `if (cond) thenExpr else elseExpr` 表达式。 */
    class IfExpr(cond: ExprNode?, thenBody: List<StmtNode>?, elseBody: List<StmtNode>?) : ExprNode {
        val cond: ExprNode = cond ?: throw NullPointerException()
        val thenBody: List<StmtNode> = ReadOnlyList(ArrayList(thenBody ?: throw NullPointerException()))
        val elseBody: List<StmtNode> = ReadOnlyList(ArrayList(elseBody ?: throw NullPointerException()))

        override fun equals(other: Any?): Boolean {
            if (other !is IfExpr) return false
            return cond == other.cond && thenBody == other.thenBody && elseBody == other.elseBody
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + cond.hashCode()
            result = 31 * result + thenBody.hashCode()
            result = 31 * result + elseBody.hashCode()
            return result
        }
    }

    /** 局部变量引用。 */
    class LocalRef(name: String?) : ExprNode {
        val name: String = name ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean = other is LocalRef && name == other.name

        override fun hashCode(): Int = name.hashCode()

        override fun toString(): String = "LocalRef(" + name + ")"
    }

    // ===== C2 新增节点 =====

    /** `[a, b, c]` 列表字面量。 */
    class ListLiteral(elements: List<ExprNode>?) : ExprNode {
        val elements: List<ExprNode> = ReadOnlyList(ArrayList(elements ?: throw NullPointerException()))

        override fun equals(other: Any?): Boolean = other is ListLiteral && elements == other.elements

        override fun hashCode(): Int = elements.hashCode()

        override fun toString(): String = "ListLiteral(" + elements + ")"
    }

    /**
     * M6-a：Map 字面量 `#{"k1": v1, "k2": v2}`。
     *
     * 键有序：runtime 求值得到 LinkedHashMap，与编译期书写顺序一致。
     * 键为字符串字面量原文（parser 已强制）。
     */
    class MapLiteral(entries: List<Entry>?) : ExprNode {
        val entries: List<Entry> = ReadOnlyList(ArrayList(entries ?: throw NullPointerException()))

        override fun equals(other: Any?): Boolean = other is MapLiteral && entries == other.entries

        override fun hashCode(): Int = entries.hashCode()

        override fun toString(): String = "MapLiteral(" + entries + ")"

        /** Map 字面量条目：键名 + 值表达式。 */
        class Entry(key: String?, value: ExprNode?) {
            val key: String = key ?: throw NullPointerException()
            val value: ExprNode = value ?: throw NullPointerException()

            override fun equals(other: Any?): Boolean {
                if (other !is Entry) return false
                return key == other.key && value == other.value
            }

            override fun hashCode(): Int {
                var result = 1
                result = 31 * result + key.hashCode()
                result = 31 * result + value.hashCode()
                return result
            }

            override fun toString(): String = "$key: $value"
        }
    }

    /** range 类型。 */
    enum class RangeKind { CLOSED, HALF_OPEN }

    /** `start..end` 或 `start until end`。 */
    class Range(start: ExprNode?, endValue: ExprNode?, kind: RangeKind?) : ExprNode {
        val start: ExprNode = start ?: throw NullPointerException()
        val endValue: ExprNode = endValue ?: throw NullPointerException()
        val kind: RangeKind = kind ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is Range) return false
            return start == other.start && endValue == other.endValue && kind == other.kind
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + start.hashCode()
            result = 31 * result + endValue.hashCode()
            result = 31 * result + kind.hashCode()
            return result
        }

        override fun toString(): String =
            "Range(" + start + (if (kind == RangeKind.CLOSED) ".." else " until ") + endValue + ")"
    }

    /** `target[index]` 索引访问。 */
    class Index(target: ExprNode?, index: ExprNode?) : ExprNode {
        val target: ExprNode = target ?: throw NullPointerException()
        val index: ExprNode = index ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is Index) return false
            return target == other.target && index == other.index
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + target.hashCode()
            result = 31 * result + index.hashCode()
            return result
        }

        override fun toString(): String = "Index(" + target + "[" + index + "])"
    }

    /**
     * `when (subject) { v1, v2 -> body ... else -> body }`.
     *
     * 同节点用于表达式与语句两种位置；语句位置丢弃返回值。
     */
    class When(subject: ExprNode?, branches: List<Branch>?, elseBody: List<StmtNode>?) : ExprNode {
        val subject: ExprNode = subject ?: throw NullPointerException()
        val branches: List<Branch> = ReadOnlyList(ArrayList(branches ?: throw NullPointerException()))

        /** null 表示无 else 分支。 */
        val elseBody: List<StmtNode>? = if (elseBody == null) null else ReadOnlyList(ArrayList(elseBody))

        fun hasElse(): Boolean = elseBody != null

        override fun equals(other: Any?): Boolean {
            if (other !is When) return false
            return subject == other.subject && branches == other.branches && elseBody == other.elseBody
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + subject.hashCode()
            result = 31 * result + branches.hashCode()
            result = 31 * result + (elseBody?.hashCode() ?: 0)
            return result
        }

        /** when 分支：`values -> body`。 */
        class Branch(values: List<ExprNode>?, body: List<StmtNode>?) {
            val values: List<ExprNode> = ReadOnlyList(ArrayList(values ?: throw NullPointerException()))
            val body: List<StmtNode> = ReadOnlyList(ArrayList(body ?: throw NullPointerException()))

            override fun equals(other: Any?): Boolean {
                if (other !is Branch) return false
                return values == other.values && body == other.body
            }

            override fun hashCode(): Int {
                var result = 1
                result = 31 * result + values.hashCode()
                result = 31 * result + body.hashCode()
                return result
            }
        }
    }

    // ===== C3 新增节点：lambda 一等公民 =====

    /**
     * lambda 表达式 `{ x, y -> body }`。
     *
     * 隐式 `it` 在编译期规范化为 `params=["it"]`。
     * body 末尾若为 ExprStmt，则其值作为 lambda 返回值（在解释器侧实现）。
     * M5-b：新增 [defaults]，与 [params] 等长；元素为默认值表达式，
     * null 表示该形参无默认值。空列表表示全部无默认值（兼容旧行为）。
     */
    class Lambda @JvmOverloads constructor(
        params: List<String>?,
        body: List<StmtNode>?,
        /** v9：是否为 suspend lambda（body 顶层位置含 await）。Stage 2 仅 wire 层流转，runtime 暂不消费。 */
        val isSuspend: Boolean = false,
        defaults: List<ExprNode?>? = emptyList(),
    ) : ExprNode {
        val params: List<String> = ReadOnlyList(ArrayList(params ?: throw NullPointerException()))
        val body: List<StmtNode> = ReadOnlyList(ArrayList(body ?: throw NullPointerException()))

        /** M5-b：默认值表达式列表，与 [params] 等长，元素 null 表示无默认值；空列表表示全部无默认值。 */
        val defaults: List<ExprNode?>

        init {
            val d = defaults ?: throw NullPointerException("defaults")
            require(d.isEmpty() || d.size == this.params.size) {
                "lambda defaults 长度与 params 不一致: " + d.size + " vs " + this.params.size
            }
            // 允许 null 元素，用 ArrayList + 只读包装（不能用 List.copyOf）。
            this.defaults = ReadOnlyList(ArrayList(d))
        }

        override fun equals(other: Any?): Boolean {
            if (other !is Lambda) return false
            return isSuspend == other.isSuspend && params == other.params &&
                body == other.body && defaults == other.defaults
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + params.hashCode()
            result = 31 * result + body.hashCode()
            result = 31 * result + isSuspend.hashCode()
            result = 31 * result + defaults.hashCode()
            return result
        }

        override fun toString(): String =
            (if (isSuspend) "SuspendLambda(" else "Lambda(") + params + " -> " + body + ")"
    }

    /**
     * `::name` 函数引用。
     *
     * M6-e：新增 [boundArgs] / [boundNamedArgs] 支持部分应用
     * `::name(args)`。语义为"创建时 snapshot 实参的 0 参可调用对象"——求值
     * FunRef 时立即对绑定实参求值得到 BoundFunRefValue，运行期调用时合并
     * 绑定实参与调用实参后转发给目标 fun。
     */
    class FunRef(
        name: String?,
        boundArgs: List<ExprNode>?,
        boundNamedArgs: List<CallExpr.NamedArg>?,
    ) : ExprNode {
        val name: String = name ?: throw NullPointerException()

        /** M6-e：创建时被 snapshot 的位置实参，空列表表示纯引用形态。 */
        val boundArgs: List<ExprNode> = ReadOnlyList(ArrayList(boundArgs ?: throw NullPointerException()))

        /** M6-e：创建时被 snapshot 的命名实参，空列表表示无命名实参。 */
        val boundNamedArgs: List<CallExpr.NamedArg> =
            ReadOnlyList(ArrayList(boundNamedArgs ?: throw NullPointerException()))

        /** 旧构造器：纯引用形态，无绑定实参；保持兼容。 */
        constructor(name: String?) : this(name, emptyList(), emptyList())

        override fun equals(other: Any?): Boolean {
            if (other !is FunRef) return false
            return name == other.name && boundArgs == other.boundArgs && boundNamedArgs == other.boundNamedArgs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + name.hashCode()
            result = 31 * result + boundArgs.hashCode()
            result = 31 * result + boundNamedArgs.hashCode()
            return result
        }

        override fun toString(): String {
            if (boundArgs.isEmpty() && boundNamedArgs.isEmpty()) {
                return "FunRef(::" + name + ")"
            }
            return "FunRef(::" + name + boundArgs +
                (if (boundNamedArgs.isEmpty()) "" else ",named=" + boundNamedArgs) + ")"
        }
    }

    /**
     * 通用调用 `callee(args...)`；trailing lambda 已在解析期合并入 args 末尾。
     * M5-b：新增 [namedArgs] 支持命名实参（`name = value`）。
     */
    class CallExpr(callee: ExprNode?, args: List<ExprNode>?, namedArgs: List<NamedArg>?) : ExprNode {
        val callee: ExprNode = callee ?: throw NullPointerException()
        val args: List<ExprNode> = ReadOnlyList(ArrayList(args ?: throw NullPointerException()))
        val namedArgs: List<NamedArg> = ReadOnlyList(ArrayList(namedArgs ?: throw NullPointerException()))

        /** 旧构造器：无命名实参；保留供回调内合成 CallExpr 等使用。 */
        constructor(callee: ExprNode?, args: List<ExprNode>?) : this(callee, args, emptyList())

        override fun equals(other: Any?): Boolean {
            if (other !is CallExpr) return false
            return callee == other.callee && args == other.args && namedArgs == other.namedArgs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + callee.hashCode()
            result = 31 * result + args.hashCode()
            result = 31 * result + namedArgs.hashCode()
            return result
        }

        override fun toString(): String =
            "CallExpr(" + callee + args + (if (namedArgs.isEmpty()) "" else ",named=" + namedArgs) + ")"

        /** M5-b：命名实参 `name = value`。 */
        class NamedArg(name: String?, value: ExprNode?) {
            val name: String = name ?: throw NullPointerException()
            val value: ExprNode = value ?: throw NullPointerException()

            override fun equals(other: Any?): Boolean {
                if (other !is NamedArg) return false
                return name == other.name && value == other.value
            }

            override fun hashCode(): Int {
                var result = 1
                result = 31 * result + name.hashCode()
                result = 31 * result + value.hashCode()
                return result
            }

            override fun toString(): String = "$name=$value"
        }
    }

    /**
     * 内建调用 `print(expr)` / `log(expr)`：由运行时转发到 DslLogger。
     *
     * M6-h Round 2：新增 [namedArgs] 字段以支持 toast(...) 命名实参；
     * 旧的内建（rgba / format / 数学函数等）保持 namedArgs 为空。
     */
    class BuiltinCall(name: String?, args: List<ExprNode>?, namedArgs: List<NamedArg>?) : ExprNode {
        val name: String = name ?: throw NullPointerException()
        val args: List<ExprNode> = ReadOnlyList(ArrayList(args ?: throw NullPointerException()))
        val namedArgs: List<NamedArg> = ReadOnlyList(ArrayList(namedArgs ?: emptyList()))

        constructor(name: String?, args: List<ExprNode>?) : this(name, args, emptyList())

        override fun equals(other: Any?): Boolean {
            if (other !is BuiltinCall) return false
            return name == other.name && args == other.args && namedArgs == other.namedArgs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + name.hashCode()
            result = 31 * result + args.hashCode()
            result = 31 * result + namedArgs.hashCode()
            return result
        }

        override fun toString(): String =
            "BuiltinCall(" + name + args + (if (namedArgs.isEmpty()) "" else ",named=" + namedArgs) + ")"

        /** 内建命名实参 `name = value`（M6-h Round 2）。 */
        class NamedArg(name: String?, value: ExprNode?) {
            val name: String = name ?: throw NullPointerException()
            val value: ExprNode = value ?: throw NullPointerException()

            override fun equals(other: Any?): Boolean {
                if (other !is NamedArg) return false
                return name == other.name && value == other.value
            }

            override fun hashCode(): Int {
                var result = 1
                result = 31 * result + name.hashCode()
                result = 31 * result + value.hashCode()
                return result
            }

            override fun toString(): String = "$name=$value"
        }
    }

    /**
     * `target is TypeName` 类型判断表达式（M5-c）。
     *
     * 第一版仅支持基础内置类型名（Number/String/Boolean/Map/List/Any/Unit），
     * 由 ExprEvaluator 按 instanceof / 数值 / 字符串 等约定执行检查。
     */
    class IsExpr(target: ExprNode?, typeName: String?) : ExprNode {
        val target: ExprNode = target ?: throw NullPointerException()
        val typeName: String = typeName ?: throw NullPointerException()

        override fun equals(other: Any?): Boolean {
            if (other !is IsExpr) return false
            return target == other.target && typeName == other.typeName
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + target.hashCode()
            result = 31 * result + typeName.hashCode()
            return result
        }

        override fun toString(): String = "IsExpr(" + target + " is " + typeName + ")"
    }

    /**
     * Stage B：`net.subscribe("ns.topic", handler)` 内置调用 IR 节点。
     *
     * 与 Lambda / FunRef / BuiltinCall 平级的独立 ExprNode 子类型，
     * 与 AST 层 `ExprAst.NetSubscribeCall` 一一对应。
     *
     * 语义：求值时 runtime 把 [handler] 求值为可调用对象（LambdaValue /
     * FunRefValue 等），注册到 `EvalContext.subscribeRouter`，返回
     * SubscriptionValue 供 DSL 持有；DSL 中可调 `s.cancel()` 取消订阅。
     */
    class NetSubscribe(namespace: String?, topic: String?, handler: ExprNode?) : ExprNode {
        val namespace: String = namespace ?: throw NullPointerException("namespace")
        val topic: String = topic ?: throw NullPointerException("topic")
        val handler: ExprNode = handler ?: throw NullPointerException("handler")

        override fun equals(other: Any?): Boolean {
            if (other !is NetSubscribe) return false
            return namespace == other.namespace && topic == other.topic && handler == other.handler
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + namespace.hashCode()
            result = 31 * result + topic.hashCode()
            result = 31 * result + handler.hashCode()
            return result
        }

        override fun toString(): String = "NetSubscribe(" + namespace + "." + topic + ", " + handler + ")"
    }
}
